import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react'

type PropertyType =
  | 'house_apartment'
  | 'house'
  | 'apartment'
  | 'commercial'
  | 'plot'

export interface RentalMarketService {
  getRentalPriceEvolutionData: (filters: any) => Promise<any>
  getRentalPriceM2EvolutionData: (filters: any) => Promise<any>
  getRentalAvailabilityData: (filters: any) => Promise<any>
  getRentalPricePerRoomData: (filters: any) => Promise<any>
  getRentalPriceM2PerRoomData: (filters: any) => Promise<any>
}

interface RentalMarketContextType {
  filters: {
    locationType: 'postal_code' | 'city' | 'district'
    propertyType: PropertyType
    location: string | null
    propertyCategory: string[]
  }
  updateLocationType: (type: 'postal_code' | 'city' | 'district') => void
  updatePropertyType: (type: PropertyType) => void
  getLocationDisplay: () => string
  service: RentalMarketService
}

const RentalMarketContext = createContext<RentalMarketContextType | undefined>(
  undefined
)

interface RentalMarketProviderProps {
  children: ReactNode
  landData: any
  service: RentalMarketService
}

export const RentalMarketProvider: React.FC<RentalMarketProviderProps> = ({
  children,
  landData,
  service,
}) => {
  const [locationType, setLocationType] = useState<
    'postal_code' | 'city' | 'district'
  >('postal_code')
  const [propertyType, setPropertyType] =
    useState<PropertyType>('house_apartment')

  const getLocation = (): string | null => {
    if (!landData) return null

    switch (locationType) {
      case 'postal_code':
        return landData.address_postal_code_name || null
      case 'city':
        return landData.municipality_name || null
      case 'district':
        return landData.district_name || null
      default:
        return null
    }
  }

  const getLocationDisplay = (): string => {
    if (!landData) return 'N/A'

    // Return the selected location level
    const location = getLocation()
    return location || 'N/A'
  }

  const getPropertyCategory = (): string[] => {
    switch (propertyType) {
      case 'house_apartment':
        return ['House', 'Apartment']
      case 'apartment':
        return ['Apartment']
      case 'house':
        return ['House']
      case 'commercial':
        return ['Commercial']
      case 'plot':
        return ['Plot']
      default:
        return []
    }
  }

  const filters = {
    locationType,
    propertyType,
    location: getLocation(),
    propertyCategory: getPropertyCategory(),
  }

  const updateLocationType = (type: 'postal_code' | 'city' | 'district') => {
    setLocationType(type)
  }

  const updatePropertyType = (type: PropertyType) => {
    setPropertyType(type)
  }

  // Update aggregations when filters change
  // TODO: Implement market aggregations context for Building Analysis
  useEffect(() => {
    const location = getLocation()
    // Market aggregations will be implemented later
    // For now, just track filter changes
  }, [locationType, propertyType, landData])

  return (
    <RentalMarketContext.Provider
      value={{
        filters,
        updateLocationType,
        updatePropertyType,
        getLocationDisplay,
        service,
      }}
    >
      {children}
    </RentalMarketContext.Provider>
  )
}

export const useRentalMarket = () => {
  const context = useContext(RentalMarketContext)
  if (!context) {
    throw new Error(
      'useRentalMarket must be used within a RentalMarketProvider'
    )
  }
  return context
}
